import { PayloadAction, SerializedError, createSlice } from '@reduxjs/toolkit';

import { apiUrlListProductsV1, apiUrlProductsByCharacterCorrelation } from '@lib/core/products/slices/urls';
import { TProductInstance } from '@lib/core/products/types';
import { createTypedAsyncThunk } from '@lib/core/service/createTypedAsyncThunk';
import { parseError } from '@lib/core/service/requests/parseError';
import { request } from '@lib/core/service/requests/request';

interface IProductsSlice {
  isLoading: boolean;
  characterCorrelationList: TProductInstance[];
  error: SerializedError;
  list: TProductInstance[];
}

const initialState: IProductsSlice = {
  characterCorrelationList: [],
  error: null,
  isLoading: false,
  list: [],
};

export const actionGetAllProducts = createTypedAsyncThunk('products/all/get', async _ => {
  const { results } = await request({
    url: apiUrlListProductsV1(),
  });

  const sortByPortfolioOrder = list =>
    list.sort(
      ({ product: prod1 }, { product: prod2 }) => prod1.attributes.portfolio_order - prod2.attributes.portfolio_order,
    );

  return { data: sortByPortfolioOrder(results) };
});

export const getProductsByCharacterCorrelation = createTypedAsyncThunk(
  'products/correlation/get',
  async ({ characterId }: { characterId: string }) => {
    const characterIdentifiers = new Set();
    let randomizedProducts = [];

    try {
      const requestData: { results: TProductInstance[] } = await request({
        url: apiUrlProductsByCharacterCorrelation(characterId),
      });
      const { results } = requestData;

      const shuffleProducts = arr => {
        const newArr = arr.slice();
        for (let i = newArr.length - 1; i > 0; i -= 1) {
          const rand = Math.floor(Math.random() * (i + 1));
          [newArr[i], newArr[rand]] = [newArr[rand], newArr[i]];
        }
        return newArr;
      };

      results.forEach((gprl: TProductInstance) => {
        characterIdentifiers.add(gprl.product.character.identifier);
      });

      // * Randomize the order of the products within each character group
      characterIdentifiers.forEach(characterIdentifier => {
        const filteredProducts = results.filter(gprl => gprl.product.character.identifier === characterIdentifier);
        randomizedProducts = [...randomizedProducts, ...shuffleProducts(filteredProducts)];
      });
    } catch (e) {
      return Promise.reject(parseError(e));
    }
    return randomizedProducts;
  },
);

export const productsSlice = createSlice({
  extraReducers: builder => {
    builder.addCase(actionGetAllProducts.pending, state => {
      state.isLoading = true;
      state.error = null;
      state.list = [];
    });
    builder.addCase(
      actionGetAllProducts.fulfilled,
      (
        state,
        action: PayloadAction<{
          data: TProductInstance[];
        }>,
      ) => {
        const { payload: { data } = {} } = action;

        state.isLoading = false;
        state.error = null;
        state.list = data;
      },
    );
    builder.addCase(actionGetAllProducts.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error;
      state.list = [];
    });
    builder.addCase(getProductsByCharacterCorrelation.pending, state => {
      state.isLoading = true;
      state.error = null;
      state.characterCorrelationList = [];
    });
    builder.addCase(getProductsByCharacterCorrelation.fulfilled, (state, action: PayloadAction<TProductInstance[]>) => {
      const { payload } = action;

      state.isLoading = false;
      state.error = null;
      state.characterCorrelationList = payload;
    });
    builder.addCase(getProductsByCharacterCorrelation.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error;
      state.characterCorrelationList = [];
    });
  },
  initialState,
  name: 'products',
  reducers: {
    actionResetCorrelatedProducts: state => {
      return {
        ...state,
        characterCorrelationList: [],
      };
    },
  },
});

export default productsSlice.reducer;
export const { actionResetCorrelatedProducts } = productsSlice.actions;
